// libs
import React, { memo, useRef, useEffect, useState, useMemo } from 'react';
import get from 'lodash/get';

// components
import FitcodeDesktop from 'shared/components/FitCode/Desktop';

// contexts
import { useResponsiveContext } from 'shared/components/ResponsiveLoader/ResponsiveContext';

// styles
import { Image, Title, Info, Section, ImagePlaceholder } from './styles';

// constants
import { IMAGE_ALT } from './constants';
import { ON_LOAD_PLACEHOLDER_COLOR } from 'shared/components/AdPlatformWidgets/constants';
import { DWEB_MAX_WIDTH } from 'shared/styles/base';
import { MARGIN_RIGHT } from 'shared/components/AdPlatformWidgets/Desktop/constant';

// helpers
import { getImageData } from './helpers';
import { getPlaceHolderColor } from 'shared/components/AdPlatformWidgets/helpers';

// defs
import { IWidgetItems } from 'shared/components/AdPlatformWidgets/types';

// hooks
import { useTrackingObserver } from 'shared/components/AdPlatformWidgets/ImpressionTracking/trackingHooks';

interface IFitcodeTileProps {
  widgetItem: IWidgetItems;
  numOfColumns: number;
  className?: string;
  childCount: number;
  contentAlign: string;
  textColor: string;
}

function FitcodeTile({
  widgetItem,
  numOfColumns,
  childCount,
  className = '',
  contentAlign,
  textColor,
}: IFitcodeTileProps) {
  const { deviceWidth } = useResponsiveContext();
  const [showImage, setShowImage] = useState(false);
  const [shouldShowFitcode, setShouldShowFitcode] = useState(false);

  const widgetRef = useRef<HTMLElement>(null);
  const trackingData = get(widgetItem, 'trackingData');
  useTrackingObserver({ widgetRef, trackingData });

  useEffect(() => {
    setShowImage(true);
  }, []);

  const containerWidth = deviceWidth > DWEB_MAX_WIDTH ? DWEB_MAX_WIDTH : deviceWidth;

  const bannerData = getImageData(widgetItem, containerWidth, numOfColumns);
  const placeholderColor = getPlaceHolderColor(childCount);

  const title = get(widgetItem, 'parameters.title') || get(widgetItem, 'parameters.title_plain');
  const description = get(widgetItem, 'parameters.description');

  const visibleMargin = (numOfColumns - 1) * MARGIN_RIGHT;

  const textStyle = useMemo(() => ({ textAlign: contentAlign }), [contentAlign]);

  const fitcode_url = get(widgetItem, 'parameters.web_action_url', '');

  const showFitcode = () => {
    setShouldShowFitcode(true);
  };

  const hideFitcode = () => {
    setShouldShowFitcode(false);
  };

  const Content = (
    <>
      {showImage ? (
        <Image
          src={bannerData.source}
          width={(containerWidth - visibleMargin) / numOfColumns}
          aspectRatio={bannerData.aspect_ratio}
          placeholderColor={placeholderColor}
          alt={IMAGE_ALT}
          onLoadPlaceholderColor={ON_LOAD_PLACEHOLDER_COLOR}
          // @ts-ignore
          srcSet={`${bannerData.source} 1x, ${bannerData.originalSource} 2x`}
        />
      ) : (
        <ImagePlaceholder bgColor={placeholderColor} aspectRatio={bannerData.aspect_ratio} />
      )}
      <div className="desc" style={textStyle}>
        {title && <Title textColor={textColor}>{title}</Title>}
        {description && <Info textColor={textColor}>{description}</Info>}
      </div>
    </>
  );

  return (
    <>
      <Section
        ref={widgetRef}
        className={className}
        onClick={showFitcode}
        numOfColumns={numOfColumns}
      >
        {Content}
      </Section>

      {shouldShowFitcode && (
        <FitcodeDesktop
          onCancel={hideFitcode}
          onOverlayHide={hideFitcode}
          fitcodeEndpoint={fitcode_url}
        />
      )}
    </>
  );
}

export default memo(FitcodeTile);
